import { z } from 'zod';

// ---------------------------------------------------------------------------
// Payment status codes (match webapp public.payment_status.payment_status_code)
// ---------------------------------------------------------------------------

export const INCOMING_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const;

export type IncomingStatusCode = (typeof INCOMING_STATUS)[keyof typeof INCOMING_STATUS];

export const INCOMING_STATUS_LABELS: Record<IncomingStatusCode, string> = {
  [INCOMING_STATUS.PENDING]: 'Pending',
  [INCOMING_STATUS.PROCESSING]: 'Processing',
  [INCOMING_STATUS.COMPLETED]: 'Completed',
  [INCOMING_STATUS.REJECTED]: 'Rejected',
  [INCOMING_STATUS.CANCELLED]: 'Cancelled',
};

// ---------------------------------------------------------------------------
// Incoming payment DTO — from GET /api/convera/incoming/payments
// ---------------------------------------------------------------------------

export const IncomingPaymentDtoSchema = z.object({
  incoming_payment_id: z.coerce.number(),
  client_id: z.coerce.number(),
  client_account_id: z.coerce.number().optional(),
  amount: z.string(),
  currency_code: z.string(),
  sender_name: z.string().nullish(),
  sender_account: z.string().nullish(),
  sender_bank: z.string().nullish(),
  sender_swift: z.string().nullish(),
  reference: z.string().nullish(),
  received_date: z.string().nullish(),
  payment_status_id: z.coerce.number(),
  payment_status: z.string(),
  transaction_id: z.string().nullish(),
  created_at: z.string(),
  updated_at: z.string(),
  // Server-side enrichment. Nullable + optional so older backends still parse
  // during the rollout window.
  client_name: z.string().nullish(),
});

export type IncomingPaymentDto = z.infer<typeof IncomingPaymentDtoSchema>;

// ---------------------------------------------------------------------------
// List response — non-authoritative meta. The webapp listing endpoint returns
// a LIMIT-capped slice without a total count, so `total` is null and the
// response is flagged `paginated: false` until the backend can return totals.
// ---------------------------------------------------------------------------

export const IncomingListResponseSchema = z.object({
  data: z.array(IncomingPaymentDtoSchema),
  meta: z.object({
    limit: z.number(),
    total: z.number().nullable(),
    paginated: z.literal(false),
  }),
});

export type IncomingListResponse = z.infer<typeof IncomingListResponseSchema>;

// ---------------------------------------------------------------------------
// Manual incoming entry receipt — POST /api/convera/incoming/manual
// Matches apps/api/src/modules/incoming/dto/manual-incoming-response.dto.ts
// ---------------------------------------------------------------------------

/** The create endpoint returns a receipt, not the enriched incoming-list DTO. */
export const ManualIncomingResponseSchema = z.object({
  incoming_payment_id: z.union([z.string(), z.number()]),
  transaction_id: z.string(),
  client_id: z.number(),
  amount: z.string(),
  currency_code: z.string(),
  sender_name: z.string().nullable(),
  status: z.string(),
  received_date: z.string(),
  created_at: z.string(),
});
export type ManualIncomingResponse = z.infer<typeof ManualIncomingResponseSchema>;

// ---------------------------------------------------------------------------
// Approve/Reject request — POST /api/convera/incoming/approve|reject
// ---------------------------------------------------------------------------

export const ApproveRejectRequestSchema = z.object({
  incoming_payment_id: z.number(),
  note: z.string().optional(),
});

export type ApproveRejectRequest = z.infer<typeof ApproveRejectRequestSchema>;

// ---------------------------------------------------------------------------
// Approve response — POST /api/convera/incoming/approve
// Matches apps/api/src/modules/incoming/dto/approve-incoming-response.dto.ts
// ---------------------------------------------------------------------------

export const ApproveIncomingResponseSchema = z.object({
  incoming_payment_id: z.coerce.number(),
  status: z.literal('credited'),
  client_id: z.coerce.number(),
  amount: z.string(),
  currency: z.string().nullable(),
  credited_account_id: z.coerce.number(),
  event: z.literal('APPROVED'),
});

export type ApproveIncomingResponse = z.infer<typeof ApproveIncomingResponseSchema>;

// ---------------------------------------------------------------------------
// Reject response — POST /api/convera/incoming/reject
// Matches apps/api/src/modules/incoming/dto/reject-incoming-response.dto.ts
// ---------------------------------------------------------------------------

export const RejectIncomingResponseSchema = z.object({
  incoming_payment_id: z.coerce.number(),
  status: z.literal('rejected'),
  event: z.literal('REJECTED'),
  reason: z.string().nullable(),
});

export type RejectIncomingResponse = z.infer<typeof RejectIncomingResponseSchema>;

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

export type IncomingStatusFilter =
  | 'all'
  | 'pending'
  | 'processing'
  | 'completed'
  | 'rejected'
  | 'cancelled';

export interface IncomingFilters {
  status: IncomingStatusFilter;
  clientId?: number;
  dateFrom?: string; // ISO date YYYY-MM-DD
  dateTo?: string; // ISO date YYYY-MM-DD
  page: number;
  limit: number;
}

export const DEFAULT_INCOMING_FILTERS: IncomingFilters = {
  status: 'all',
  page: 1,
  limit: 50,
};

// ---------------------------------------------------------------------------
// Simulate incoming wire request — POST /api/convera/incoming/simulate
// ---------------------------------------------------------------------------

export const SimulateIncomingRequestSchema = z.object({
  client_id: z
    .number({ required_error: 'Client is required' })
    .int()
    .positive('Client ID must be positive'),
  amount: z
    .string({ required_error: 'Amount is required' })
    .min(1, 'Amount is required')
    .refine((val) => {
      try {
        const n = Number(val);
        return !isNaN(n) && n > 0;
      } catch {
        return false;
      }
    }, 'Amount must be a positive number'),
  currency_code: z
    .string({ required_error: 'Currency is required' })
    .length(3, 'Currency must be a 3-letter ISO code'),
  sender_name: z
    .string({ required_error: 'Sender name is required' })
    .min(1, 'Sender name is required')
    .max(100),
  sender_account: z.string().max(50).optional().or(z.literal('')),
  reference: z.string().max(50).optional().or(z.literal('')),
});

export type SimulateIncomingRequest = z.infer<typeof SimulateIncomingRequestSchema>;

// ---------------------------------------------------------------------------
// Simulate incoming wire response
// ---------------------------------------------------------------------------

export const SimulateIncomingResponseSchema = z.object({
  incoming_payment_id: z.number(),
  transaction_id: z.string(),
  status: z.string(),
  amount: z.string(),
  currency_code: z.string(),
  sender_name: z.string(),
  created_at: z.string(),
});

export type SimulateIncomingResponse = z.infer<typeof SimulateIncomingResponseSchema>;

// ---------------------------------------------------------------------------
// Simple client option for Combobox
// ---------------------------------------------------------------------------

export interface ClientOption {
  id: number;
  label: string; // company_name
}

// ---------------------------------------------------------------------------
// /api/clients response — coerced because the webapp serialises client_id as
// a string (Postgres bigint via MikroORM). z.coerce.number() accepts both
// representations and fails on anything else.
// ---------------------------------------------------------------------------

export const ClientListItemSchema = z.object({
  client_id: z.coerce.number(),
  company_name: z.string(),
});

export const ClientListResponseSchema = z.object({
  items: z.array(ClientListItemSchema),
});
